// Shared Firebase Authentication Configuration
// Link: /src/HelloCashflowDomain/Authentication/services/firebase.ts
// This file provides a centralized Firebase initialization that both apps can use

import type { FirebaseApp } from "firebase/app";
import { getApp, getApps, initializeApp } from "firebase/app";
import type { Auth } from "firebase/auth";
import { connectAuthEmulator } from "firebase/auth";
import type { Firestore } from "firebase/firestore";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import type { Functions } from "firebase/functions";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import { createAdminAuth } from "@/lib/firebase/createAdminAuth";
import {
  connectLocalAdminFirebaseEmulator,
  readLocalAdminFirebaseEmulator,
} from "@/lib/firebase/localFirebaseEmulator";

const logger =
  FrontendLoggerFactory.getLogger().withComponent("FirebaseService");

// Firebase configuration interface
export interface FirebaseConfig {
  apiKey: string;
  appId: string;
  authDomain: string;
  messagingSenderId: string;
  projectId: string;
  storageBucket: string;
}

// Singleton instances
let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;
let firebaseFirestore: Firestore | null = null;
let firebaseFunctions: Functions | null = null;

// Global initialization promise tracking
let initializationPromise: Promise<void> | null = null;
let resolveInitialization: (() => void) | null = null;
let rejectInitialization: ((error: Error) => void) | null = null;
let localInitializationFailure: Error | null = null;

// Create the initialization promise
const createInitializationPromise = () => {
  initializationPromise ??= new Promise<void>((resolve, reject) => {
    resolveInitialization = resolve;
    rejectInitialization = reject;
  });
  // Startup can throw before a waiter attaches. Keep the original promise
  // rejected for callers without emitting an unhandled rejection meanwhile.
  void initializationPromise.catch(() => undefined);
  return initializationPromise;
};

const recordLocalInitializationFailure = () => {
  localInitializationFailure ??= new Error(
    "Local Admin Firebase emulator initialization failed; restart the owned runtime.",
  );
  rejectInitialization?.(localInitializationFailure);
  rejectInitialization = null;
  resolveInitialization = null;
};

/**
 * Initializes Firebase with the provided configuration
 * This function should be called once at the start of your application
 * @param config - Firebase configuration object
 * @param options - Optional configuration for emulators and other settings
 * @returns Firebase services object
 */
export const initializeFirebaseAuth = (
  config?: FirebaseConfig,
  options?: {
    useEmulators?: boolean;
    functionsRegion?: string;
    emulatorPorts?: {
      auth?: number;
      firestore?: number;
      functions?: number;
    };
  },
) => {
  if (localInitializationFailure) throw localInitializationFailure;

  // Create the initialization promise if it doesn't exist
  void createInitializationPromise();

  // Use provided config or fall back to environment variables
  const firebaseConfig: FirebaseConfig = config ?? {
    apiKey: import.meta.env.VITE_FIREBASE_APIKEY,
    appId: import.meta.env.VITE_FIREBASE_APPID,
    authDomain: import.meta.env.VITE_FIREBASE_AUTHDOMAIN,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGINGSENDERID,
    projectId: import.meta.env.VITE_FIREBASE_PROJECTID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGEBUCKET,
  };

  const localModeRequested =
    import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST !== undefined ||
    (typeof firebaseConfig.projectId === "string" &&
      firebaseConfig.projectId.startsWith("demo-"));

  // Validate required configuration
  const requiredFields = ["apiKey", "authDomain", "projectId", "appId"];
  for (const field of requiredFields) {
    if (!firebaseConfig[field as keyof FirebaseConfig]) {
      logger.error("Firebase configuration validation failed", undefined, {
        operation: "firebase_config_validation_failed",
        missingField: field,
      });
      if (localModeRequested) recordLocalInitializationFailure();
      throw new Error(`Firebase configuration missing: ${field}`);
    }
  }

  let localEmulator: ReturnType<typeof readLocalAdminFirebaseEmulator>;
  try {
    localEmulator = readLocalAdminFirebaseEmulator({
      projectId: firebaseConfig.projectId,
      host: import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST,
      development: import.meta.env.DEV,
      pageHostname:
        typeof window === "undefined" ? undefined : window.location.hostname,
    });
  } catch (error) {
    recordLocalInitializationFailure();
    throw error;
  }

  // Keep candidates private until all required local bindings succeed. A
  // partial connection must not publish cloud-capable service getters.
  let candidates: {
    app: FirebaseApp;
    auth: Auth;
    firestore: Firestore;
    functions: Functions;
  };
  let connectionAttempted = false;
  try {
    const app =
      firebaseApp ??
      (getApps().some((candidate) => candidate.name === "[DEFAULT]")
        ? getApp()
        : initializeApp(firebaseConfig));
    if (localEmulator && app.options.projectId !== localEmulator.projectId) {
      throw new Error(
        "Local Admin Firebase app does not match the demo project.",
      );
    }
    const auth = firebaseAuth ?? createAdminAuth(app);
    const firestore = firebaseFirestore ?? getFirestore(app);
    const functions =
      firebaseFunctions ??
      getFunctions(app, options?.functionsRegion ?? "australia-southeast1");
    connectionAttempted = true;
    connectLocalAdminFirebaseEmulator(
      { auth, firestore, functions },
      localEmulator,
    );
    candidates = { app, auth, firestore, functions };
  } catch (error) {
    if (Boolean(localEmulator) || connectionAttempted)
      recordLocalInitializationFailure();
    throw error;
  }
  firebaseApp = candidates.app;
  firebaseAuth = candidates.auth;
  firebaseFirestore = candidates.firestore;
  firebaseFunctions = candidates.functions;

  // Connect to emulators if specified (for development)
  if (!localEmulator && options?.useEmulators && import.meta.env.DEV) {
    const emulatorPorts = options.emulatorPorts ?? {};

    try {
      if (emulatorPorts.auth) {
        connectAuthEmulator(
          firebaseAuth,
          `http://localhost:${emulatorPorts.auth}`,
          {
            disableWarnings: true,
          },
        );
      }

      if (emulatorPorts.firestore) {
        connectFirestoreEmulator(
          firebaseFirestore,
          "localhost",
          emulatorPorts.firestore,
        );
      }

      if (emulatorPorts.functions) {
        connectFunctionsEmulator(
          firebaseFunctions,
          "localhost",
          emulatorPorts.functions,
        );
      }
    } catch (error) {
      // Emulator connection errors are non-fatal
      logger.warn("Failed to connect to Firebase emulators:", { error });
    }
  }

  // Wait for auth to be ready, then resolve the initialization promise
  if (resolveInitialization) {
    logger.info("Firebase auth readiness lifecycle started", {
      operation: "firebase_auth_ready_init",
    });
    void firebaseAuth
      .authStateReady()
      .then(() => {
        logger.info("Firebase auth readiness lifecycle completed", {
          operation: "firebase_auth_ready_complete",
        });
        if (resolveInitialization) {
          resolveInitialization();
          resolveInitialization = null; // Prevent multiple resolutions
        }
      })
      .catch((error) => {
        if (localEmulator) {
          recordLocalInitializationFailure();
          logger.error(
            "Local Firebase Auth readiness failed",
            localInitializationFailure ?? undefined,
          );
          return;
        }
        logger.error("Firebase Auth initialization failed:", error as Error);
        logger.info(
          "Firebase auth readiness lifecycle completed with error fallback",
          {
            operation: "firebase_auth_ready_error_resolved",
          },
        );
        if (resolveInitialization) {
          resolveInitialization(); // Resolve anyway to prevent hanging
          resolveInitialization = null;
        }
      });
  }

  return {
    app: firebaseApp,
    auth: firebaseAuth,
    firestore: firebaseFirestore,
    functions: firebaseFunctions,
  };
};

/**
 * Returns a promise that resolves when Firebase is fully initialized
 * Components should await this before using any Firebase functionality
 */
export const waitForFirebaseInit = (): Promise<void> => {
  if (localInitializationFailure)
    return Promise.reject(localInitializationFailure);
  return initializationPromise ?? Promise.resolve();
};

/**
 * Gets the current Firebase Auth instance
 * Throws an error if Firebase hasn't been initialized
 */
export const getFirebaseAuth = (): Auth => {
  if (localInitializationFailure) throw localInitializationFailure;
  if (!firebaseAuth) {
    throw new Error(
      "Firebase Auth not initialized. Call initializeFirebaseAuth() first.",
    );
  }
  return firebaseAuth;
};

/**
 * Gets the current Firebase Firestore instance
 * Throws an error if Firebase hasn't been initialized
 */
export const getFirebaseFirestore = (): Firestore => {
  if (localInitializationFailure) throw localInitializationFailure;
  if (!firebaseFirestore) {
    throw new Error(
      "Firebase Firestore not initialized. Call initializeFirebaseAuth() first.",
    );
  }
  return firebaseFirestore;
};

/**
 * Gets the current Firebase Functions instance
 * Throws an error if Firebase hasn't been initialized
 */
export const getFirebaseFunctions = (): Functions => {
  if (localInitializationFailure) throw localInitializationFailure;
  if (!firebaseFunctions) {
    throw new Error(
      "Firebase Functions not initialized. Call initializeFirebaseAuth() first.",
    );
  }
  return firebaseFunctions;
};

/**
 * Gets the current Firebase App instance
 * Throws an error if Firebase hasn't been initialized
 */
export const getFirebaseApp = (): FirebaseApp => {
  if (localInitializationFailure) throw localInitializationFailure;
  if (!firebaseApp) {
    throw new Error(
      "Firebase not initialized. Call initializeFirebaseAuth() first.",
    );
  }
  return firebaseApp;
};

/**
 * Resets the Firebase instances (useful for testing)
 * WARNING: This should only be used in test environments
 */
export const resetFirebaseInstances = (): void => {
  if (import.meta.env.NODE_ENV !== "test") {
    logger.warn(
      "resetFirebaseInstances should only be used in test environments",
    );
  }

  firebaseApp = null;
  firebaseAuth = null;
  firebaseFirestore = null;
  firebaseFunctions = null;
};

// Default export for convenience
export default {
  getFirebaseApp,
  getFirebaseAuth,
  getFirebaseFirestore,
  getFirebaseFunctions,
  initializeFirebaseAuth,
  resetFirebaseInstances,
};
